import type { FC } from "react";
import { useState, useCallback } from "react";
import { View, type LayoutChangeEvent } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Button, Text, IconButton, Icon, Skeleton, SkeletonGroup } from "@visioni/a3s-ui/components";
import { isCancellable, queuedStatusDetail } from "../../helpers/queue-status-label";

export type CompilationRowStatus = "ready" | "processing" | "queued" | "uploading" | "failed";

export interface CompilationRowProps {
  readonly status: CompilationRowStatus;
  /** A `queued` job's place in its camera's queue; see `CompilationListItem.queuePosition`. */
  readonly queuePosition?: number | undefined;
  /**
   * Why the compilation failed, in prose. Shown only alongside a `failed`
   * status — an operator seeing "Failed" with no explanation has nothing to
   * act on, and most of these reasons are things they can fix themselves.
   */
  readonly failureReason?: string | undefined;
  /** Display name of the camera the compilation was requested for. */
  readonly cameraLabel: string;
  /** When the compilation was requested (ISO string or formatted display string). */
  readonly requestedAt: string;
  /** Start of the requested compilation range (display string). */
  readonly rangeStart: string;
  /** End of the requested compilation range (display string). */
  readonly rangeEnd: string;
  readonly onWatch?: (() => void) | undefined;
  readonly onDownload?: (() => void) | undefined;
  /**
   * Cancels the compilation, after the reader confirms. Offered only while it
   * waits in the cloud; omit it for readers who may not cancel.
   */
  readonly onCancel?: (() => void) | undefined;
  /** True while a cancel is in flight for this row. */
  readonly cancelling?: boolean | undefined;
}

/**
 * Asks before cancelling, in place of the row's action buttons, as a3s-ui has
 * no confirm dialog.
 */
function CancelPrompt({
  cancelling,
  onKeep,
  onConfirm,
}: {
  readonly cancelling: boolean;
  readonly onKeep: () => void;
  readonly onConfirm: () => void;
}) {
  return (
    <View style={styles.cancelPrompt} accessibilityRole="alert">
      <View style={styles.cancelButtons}>
        <Button label="Keep" variant="link" variantColor="grey" size="sm" disabled={cancelling} onPress={onKeep} />
        <Button
          label="Cancel compilation"
          variant="secondary"
          variantColor="danger"
          size="sm"
          loading={cancelling}
          loadingText="Cancelling…"
          onPress={onConfirm}
        />
      </View>
    </View>
  );
}

/**
 * Web only: shows "Cancel" on hover. a3s-ui has no tooltip, and `IconButton`
 * spends its `title` on the accessibility label, so the hover text is set on
 * the wrapping element.
 */
function setCancelTooltip(node: unknown): void {
  if (typeof HTMLElement !== "undefined" && node instanceof HTMLElement) node.title = "Cancel";
}

const statusConfig: Record<CompilationRowStatus, { readonly label: string; readonly dotColor: string }> = {
  ready: { label: "Ready", dotColor: "#10B981" },
  processing: { label: "Processing", dotColor: "#3B82F6" },
  queued: { label: "Queued", dotColor: "#3B82F6" },
  uploading: { label: "Uploading", dotColor: "#3B82F6" },
  failed: { label: "Failed", dotColor: "#EF4444" },
};

const WIDE_BREAKPOINT = 500;

const DOT_SIZE = 8;

/** An `IconButton` at size `sm`. */
const ACTION_BUTTON_SIZE = 32;

const CompilationRow: FC<CompilationRowProps> = ({
  status,
  queuePosition,
  failureReason,
  cameraLabel,
  requestedAt,
  rangeStart,
  rangeEnd,
  onWatch,
  onDownload,
  onCancel,
  cancelling,
}) => {
  const { theme } = useUnistyles();
  const { dotColor } = statusConfig[status];
  const { label } = statusConfig[status];
  const detail = status === "queued" ? queuedStatusDetail(queuePosition) : undefined;
  const actionsEnabled = status === "ready";
  // A ready job can still lack a download URL (e.g. a not-yet-redeployed
  // Lambda that only signs `url`) — `onDownload` is only passed by the caller
  // when one exists, so the button disables itself rather than being a dead
  // no-op click.
  const watchEnabled = actionsEnabled && !!onWatch;
  const downloadEnabled = actionsEnabled && !!onDownload;
  const [wide, setWide] = useState(false);

  const handleLayout = useCallback((e: LayoutChangeEvent) => {
    setWide(e.nativeEvent.layout.width >= WIDE_BREAKPOINT);
  }, []);

  // Held here rather than in a child, which remounts when the row switches
  // between its wide and narrow layouts.
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const cancellable = isCancellable({ status, queuePosition });
  // Reset once the job is sent, so one later put back to waiting does not
  // return already confirming.
  if (confirmingCancel && !cancellable) setConfirmingCancel(false);

  // Gone by itself once the job is sent, as the device cannot be told to stop.
  const canCancel = !!onCancel && cancellable;
  const promptingCancel = canCancel && (confirmingCancel || cancelling === true);
  // A queued job has no video yet, so watch and download are left out rather than disabled.
  const showsVideoActions = status !== "queued";

  const failureNote =
    failureReason && status === "failed" ?
      <View
        style={[styles.warningBubble, { borderColor: theme.colors.danger[400] }]}
        accessibilityRole="alert"
        accessibilityLabel={failureReason}
      >
        <Icon name="warningOutline" size={12} color={theme.colors.danger[400]} />
        <Text textStyle="xs/medium" style={{ color: theme.colors.danger[400] }} numberOfLines={2}>
          {failureReason}
        </Text>
      </View>
    : null;

  const cancelPrompt =
    promptingCancel ?
      <CancelPrompt
        cancelling={cancelling ?? false}
        onKeep={() => setConfirmingCancel(false)}
        onConfirm={() => {
          setConfirmingCancel(false);
          onCancel?.();
        }}
      />
    : null;

  const actionButtons = (
    <View style={styles.actionsCell}>
      {showsVideoActions ?
        <>
          <IconButton
            Icon={() => <Icon name="play" size={16} color={theme.colors.text} />}
            title="Watch compilation"
            disabled={!watchEnabled}
            size="sm"
            style={!watchEnabled ? styles.actionButtonDisabled : undefined}
            {...(watchEnabled ? { onPress: () => onWatch?.() } : {})}
          />
          <IconButton
            Icon={() => <Icon name="download" size={16} color={theme.colors.text} />}
            title="Download compilation"
            disabled={!downloadEnabled}
            size="sm"
            style={!downloadEnabled ? styles.actionButtonDisabled : undefined}
            {...(downloadEnabled ? { onPress: () => onDownload?.() } : {})}
          />
        </>
      : null}
      {canCancel ?
        <View ref={setCancelTooltip}>
          <IconButton
            Icon={() => <Icon name="close" size={16} color={theme.colors.text} />}
            title="Cancel queued compilation"
            size="sm"
            onPress={() => setConfirmingCancel(true)}
          />
        </View>
      : null}
    </View>
  );

  const statusContent = (
    <View style={styles.statusStack}>
      <View style={styles.statusCell}>
        <View style={[styles.dot, { backgroundColor: dotColor }]} />
        <Text textStyle="sm/medium" color="text">
          {label}
        </Text>
      </View>
      {detail ?
        <Text textStyle="xs/regular" color="textMuted" style={styles.statusDetail}>
          {detail}
        </Text>
      : null}
    </View>
  );

  if (wide) {
    return (
      <View style={styles.containerWide} onLayout={handleLayout}>
        {statusContent}

        <View style={styles.cameraCell}>
          <Text textStyle="sm/regular" color="text" numberOfLines={1}>
            {cameraLabel}
          </Text>
        </View>

        <View style={styles.detailsCellWide}>
          <Text textStyle="sm/regular" color="text" numberOfLines={1}>
            {rangeStart} – {rangeEnd}
          </Text>
          <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
            Requested {requestedAt}
          </Text>
          {failureNote}
        </View>

        {/* Wide rows ask in place of the buttons, which keeps the row one line high. */}
        {cancelPrompt ?? actionButtons}
      </View>
    );
  }

  return (
    <View style={styles.container} onLayout={handleLayout}>
      {/* The prompt takes the buttons' place beside the status, wrapping under it where it cannot fit. */}
      <View style={styles.topRow}>
        {statusContent}
        {cancelPrompt ?? actionButtons}
      </View>

      <View style={styles.detailsCell}>
        <Text textStyle="sm/medium" color="text" numberOfLines={1}>
          {cameraLabel}
        </Text>
        <View style={styles.dateGroup}>
          <Text textStyle="sm/regular" color="text" numberOfLines={1}>
            {rangeStart} - {rangeEnd}
          </Text>
          <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
            Requested {requestedAt}
          </Text>
          {failureNote}
        </View>
      </View>
    </View>
  );
};

CompilationRow.displayName = "CompilationRow";

export { CompilationRow };

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

const CompilationRowSkeleton: FC = () => {
  const [wide, setWide] = useState(false);

  const handleLayout = useCallback((e: LayoutChangeEvent) => {
    setWide(e.nativeEvent.layout.width >= WIDE_BREAKPOINT);
  }, []);

  if (wide) {
    return (
      <SkeletonGroup>
        <View style={styles.containerWide} onLayout={handleLayout}>
          <View style={styles.statusStack}>
            <View style={styles.statusCell}>
              <Skeleton variant="circular" width={8} height={8} />
              <Skeleton variant="text" width={70} textStyle="sm/medium" />
            </View>
          </View>
          <View style={styles.cameraCell}>
            <Skeleton variant="text" width="70%" textStyle="sm/regular" />
          </View>
          <View style={styles.detailsCellWide}>
            <Skeleton variant="text" width="80%" textStyle="sm/regular" />
            <Skeleton variant="text" width="50%" textStyle="xs/regular" />
          </View>
          <View style={styles.actionsCell}>
            <Skeleton variant="rounded" width={28} height={28} />
            <Skeleton variant="rounded" width={28} height={28} />
          </View>
        </View>
      </SkeletonGroup>
    );
  }

  return (
    <SkeletonGroup>
      <View style={styles.container} onLayout={handleLayout}>
        <View style={styles.topRow}>
          <View style={styles.statusCell}>
            <Skeleton variant="circular" width={8} height={8} />
            <Skeleton variant="text" width={70} textStyle="sm/medium" />
          </View>
          <View style={styles.actionsCell}>
            <Skeleton variant="rounded" width={28} height={28} />
            <Skeleton variant="rounded" width={28} height={28} />
          </View>
        </View>
        <View style={styles.detailsCell}>
          <Skeleton variant="text" width="60%" textStyle="sm/medium" />
          <View style={styles.dateGroup}>
            <Skeleton variant="text" width="80%" textStyle="sm/regular" />
            <Skeleton variant="text" width="50%" textStyle="xs/regular" />
          </View>
        </View>
      </View>
    </SkeletonGroup>
  );
};

CompilationRowSkeleton.displayName = "CompilationRowSkeleton";

export { CompilationRowSkeleton };

const styles = StyleSheet.create((theme) => ({
  container: {
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.lg,
    backgroundColor: theme.colors.surfaceCard,
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    gap: theme.spacing.md,
  },
  containerWide: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.lg,
    backgroundColor: theme.colors.surfaceCard,
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    gap: theme.spacing.xl,
  },
  topRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    rowGap: theme.spacing.sm,
  },
  statusCell: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
    minWidth: 100,
  },
  // Wide enough for "Waiting for device", so the camera column lines up across rows.
  statusStack: {
    gap: theme.spacing.xxs,
    minWidth: 140,
  },
  // Lines the detail up under the label, past the dot and its gap.
  statusDetail: {
    marginLeft: DOT_SIZE + theme.spacing.md,
  },
  cameraCell: {
    minWidth: 120,
    maxWidth: 220,
    flex: 0.5,
  },
  dot: {
    width: DOT_SIZE,
    height: DOT_SIZE,
    borderRadius: DOT_SIZE / 2,
  },
  detailsCell: {
    gap: theme.spacing.xs,
  },
  dateGroup: {
    gap: theme.spacing.xxs,
  },
  detailsCellWide: {
    flex: 1,
    minWidth: 0,
    gap: theme.spacing.xxs,
  },
  // Sized for two buttons whatever it holds, so a queued row with one button
  // or none keeps its height and lines its columns up with the other rows.
  actionsCell: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: theme.spacing.xs,
    minWidth: 2 * ACTION_BUTTON_SIZE + theme.spacing.xs,
    minHeight: ACTION_BUTTON_SIZE,
  },
  actionButtonDisabled: {
    opacity: 0.35,
  },
  // `marginLeft: "auto"` keeps the prompt right-aligned when it wraps onto its own line.
  cancelPrompt: {
    marginLeft: "auto",
    alignItems: "flex-end",
  },
  cancelButtons: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.sm,
  },
  // `alignSelf` keeps the bubble hugging its text instead of stretching to
  // the full width of the details column. Border colour is applied inline
  // from the theme's danger token.
  warningBubble: {
    alignSelf: "flex-start",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.xs,
    marginTop: theme.spacing.xs,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xxs,
    borderRadius: theme.spacing.lg,
    borderWidth: 1,
  },
}));
